import { AppData, Lightning, Utils } from '@lightningjs/sdk'
import { AppSetup } from '../../lib/app-setup'
import { Helper } from '../../lib/helper'
import { AppApiConfig } from '../../lib/app-api-config'
import { Logger } from '../../lib/logger'
import { ISO_LANGUAGE_MAP } from '../../lib/enum'

export interface LanguagePanelTemplateSpec extends Lightning.Component.TemplateSpecLoose {}

export default class LanguagePanel
    extends Lightning.Component<LanguagePanelTemplateSpec>
    implements Lightning.Component<LanguagePanelTemplateSpec>
{
    private selectedIndex = 0
    languages: any[] = []

    setLanguages(apiLangs: any[]) {
        this.languages = apiLangs.map((lang, idx) => ({
            ref: 'Lang' + idx,
            label: lang.language,
            value: lang.value || lang.language,
            langDescription: lang.file_path
        }))
        this._buildItems()
        this.patch({ h: this.languages.length * 68 }) // 64 item height + 4 margin
    }

    static override _template(): Lightning.Component.Template<LanguagePanelTemplateSpec> {
        return {
            autoResize: true,
            collision: true,
            rect: true,
            color: 0x00000000, 
            flex: { direction: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' },
            Items: {
                autoResize: true,
                collision: true,
                flex: { direction: 'column' },
            },
        }
    }

    override _init() {
        // this._buildItems()
    }

    override _enable() {
        const { appState } = AppData as AppSetup
        const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!
        this.patch({ color: Helper.getHexaColor(appApiConfig?.appLayoutConfig.textfocuscolor) })
    }

    _buildItems() {
        const children = this.languages.map((lang, idx) => ({
            ref: lang.ref,
            w: 295,
            h: 68,
            collision: true,
            rect: true,
            zIndex:120,
            Label: {
                x: 30,
                y: 32,
                mountY: 0.5,
                text: {
                    text: lang.label,
                    fontSize: 26,
                    textColor: 0xffffffff,
                    fontFace: Helper.getFontFamily('Regular'),
                },
            },
            Tick: {
                src: Utils.asset('images/tick.png'),
                x: 235,
                y: 32,  
                mountY: 0.5,
                visible: false,
            },
        }))
        this.tag('Items').patch({ children })
    }

    _updateTick() {
        const currentLang = (Helper.getCookie('selectedDescriptionLanguage') || 'en').toLowerCase()
        const hasMatch = this.languages.some(l => l.value.toLowerCase() === currentLang)
        this.languages.forEach((lang, idx) => {
            const item = this.tag('Items').tag(lang.ref)
            if (!item) return
            const isActive = hasMatch
                ? lang.value.toLowerCase() === currentLang
                : idx === 0
            item.tag('Tick').visible = isActive
        })
    }

    _updateFocus() {
        const { platform } = AppData as AppSetup
        this.languages.forEach((lang, idx) => {
            const item = this.tag('Items').tag(lang.ref)
            if (!item) return
            const isFocused = idx === this.selectedIndex
            item.patch({ color: isFocused ? 0x330D0D1D : undefined })
            if (isFocused) platform?.textToSpeach(lang.label)
        })
    }

    /** Called from parent when this panel is shown; resets focus to active language */
    open() {
        if (!this.languages.length) return
        const currentLang = (Helper.getCookie('selectedDescriptionLanguage') || 'en').toLowerCase()
        const matchIdx = this.languages.findIndex(l => l.value.toLowerCase() === currentLang)
        this.selectedIndex = matchIdx !== -1 ? matchIdx : 0
        this._updateFocus()
        this._updateTick()
    }

    override _focus() {
        this._updateFocus()
    }

    override _handleUp() {
        if (this.selectedIndex > 0) {
            this.selectedIndex--
            this._updateFocus()
        }
        return true
    }

    override _handleDown() {
        if (this.selectedIndex < this.languages.length - 1) {
            this.selectedIndex++
            this._updateFocus()
        }
        return true
    }

    override _handleLeft() {
        // signal parent to return focus to main options list
        this.signal('close')
        return true
    }

    override _handleRight() {
        return true
    }

    override _handleBack() {
        this.signal('close')
        return true
    }

    override _handleEnter() {
        this._selectCurrent()
        return true
    }

    _handleHover(target: any) {
        if (!target || !target.__ref) return
        const idx = this.languages.findIndex(l => l.ref === target.__ref)
        if (idx !== -1) {
            this.selectedIndex = idx
            this._updateFocus()
        }
    }

    _handleClick(target: any) {
        if (!target || !target.__ref) return
        const idx = this.languages.findIndex(l => l.ref === target.__ref)
        Logger.printLog('info','[LanguagePanel] _handleClick called, target:', target)
        if (idx !== -1) {
            this.selectedIndex = idx
            this._updateFocus()
            this._selectCurrent()
        }
    }

    _selectCurrent() {
        const lang = this.languages[this.selectedIndex]
        if (!lang) return
        const { platform } = AppData as AppSetup
        platform?.textToSpeach(lang.label)
        Helper.setGeneralCookie('selectedDescriptionLanguage', lang.value.toLowerCase(), 365)
        const labelEng = ISO_LANGUAGE_MAP[lang.value.toLowerCase() as keyof typeof ISO_LANGUAGE_MAP] || lang.value
        Logger.processUserAction(`descriptionLanguageChanged-"${labelEng}"`)
        this._updateTick()
        this.signal('languageSelected')
    }
}
